import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { CompanyDTO, CompanyFilterDTO, ProductDTO, ProductFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId } from '../../../shared/environment';
import { People } from '../people/People';
import { ProductMaintenance } from './ProductMaitenance';

export interface IProductProps {
    data: any
}

export const Product: React.FC<IProductProps> = ( { data }) => {

    const [openingList, setOpeningList] = useState<ValueListDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [productFilterDTO, setProductFilterDTO] = useState<ProductFilterDTO>();
    const [productList, setProductList] = useState<ProductDTO[]>([]);

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setProductFilterDTO({
            ...productFilterDTO,
            id:'',
            code: '',
            laboratory: '',
            salePerspectiveId: '',
            companyId: '',
        } as ProductFilterDTO);
    }, []);

    useEffect(() => {
        handleListGetAll(ListId.corpsphere.product.printTypeId, setOpeningList);
        handleCompanyGetAll();
        handleProductGetAllViewAsync();
    }, [productFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleProductGetAllViewAsync();
    }, [actionExecuted]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);
    
    const handleProductGetAllViewAsync = useCallback(async () => {
        if (productFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('corpsphere.product', productFilterDTO, setActionExecuted, setLoading, setProductList, showMessage, true);
    }, [productFilterDTO]);

    const handleClearFilterClick = () => {
        setProductFilterDTO({
            ...productFilterDTO,
            id:'',
            code: '',
            laboratory: '',
            salePerspectiveId: '',
            companyId: '',
        } as ProductFilterDTO);
    };

    function handleSearchClick() {
        handleProductGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Product', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: productList.length },
        { label: 'Laboratory', value: productFilterDTO?.name },
        { label: 'Opening', value: productList.find(x => x.id === productFilterDTO?.companyId)?.name },
        { label: 'Company', value: companyList.find(x => x.id === productFilterDTO?.companyId)?.name },
    ];

    const columns: ICardColumns[] = [
        { field: 'code', label: 'Code', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'name', label: 'Name', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'printType', label: 'Print Type', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'objective', label: 'ObjectiveId', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'quantity', label: 'Quantity', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'support', label: 'Support', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'line', label: 'Line', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'drying', label: 'Drying', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'screenAniloxNumber', label: 'Screen / Anilox N', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='COMPANY'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setProductFilterDTO}
                                value={productFilterDTO?.name}
                                obj={productFilterDTO}
                                placeholder={'Laboratory'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='openingId'
                                label='Opening'
                                setValue={setProductFilterDTO}
                                value={productFilterDTO?.openingId}
                                obj={productFilterDTO}
                                valueList={openingList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                setValue={setProductFilterDTO}
                                value={productFilterDTO?.companyId}
                                obj={productFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={productList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <ProductMaintenance
                        data={''}
                        parentId={data.id}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <ProductMaintenance
                        data={editRecord}
                        parentId={data.id}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'corpsphere.product'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};